import React, { useEffect, useState } from "react";
import "../styles/projectdetail.css";
import { NavLink, useParams } from "react-router-dom";
import { popularProducts } from "../projectData.js";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactGA from "react-ga4";

function ProjectDetail() {
  const { id } = useParams();
  const [lightboxImg, setLightboxImg] = useState(null);

  const thisProduct = popularProducts.find((prod) => prod.id === id);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: `Project Detail:${thisProduct?.text}-${id}`,
    });
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setLightboxImg(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const pType = thisProduct.type;
  const cstyle = thisProduct.cstyle;
  const mcolor = thisProduct.colors;
  const achieve = thisProduct.achievements;
  const engNotes = thisProduct.engNotes; // replaces `challenges` — add this field to projectData
  const links = thisProduct.linklist;
  const techstack = thisProduct.techstack;
  const desc = thisProduct.desc;
  const extraimgs = thisProduct.extra_imgs;
  const role = thisProduct.role; // add this field to projectData

  return (
    <div className="dt-div fade">
      {/* ── Back link ── */}
      <NavLink to="/projects" className="dt-back-link">
        <ArrowBackIcon className="dt-back-icon" />
        <span>All Projects</span>
      </NavLink>

      {/* ── Project header ── */}
      <div className="dt-header">
        <h2 className="dt-project-label">
          <span aria-hidden="true">/</span> {thisProduct?.text}
        </h2>
        <p className="dt-subtext">{thisProduct?.subtext}</p>
      </div>

      {/* ── Hero: image + metadata ── */}
      <div className="dt-hero-grid">
        {/* Left: main screenshot */}
        <div className="dt-img-wrap">
          <img
            className="dt-hero-img"
            src={thisProduct?.img}
            alt={thisProduct?.subtext}
          />
        </div>

        {/* Right: metadata panel */}
        <div className="dt-meta-panel">
          {/* Role */}
          {role && (
            <div className="dt-meta-section">
              <p className="dt-meta-label">Role</p>
              <span className="dt-role-badge">{role}</span>
            </div>
          )}

          {/* Links */}
          {Object.keys(links).length > 0 && (
            <div className="dt-meta-section">
              <p className="dt-meta-label">Links</p>
              <div className="dt-links">
                {Object.keys(links).map((key, i) => (
                  <a
                    key={i}
                    href={links[key]}
                    target="_blank"
                    rel="noreferrer"
                    className="dt-link"
                  >
                    {key}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Tech stack */}
          <div className="dt-meta-section">
            <p className="dt-meta-label">Tech Stack</p>
            <div className="dt-tags">
              {techstack.map((skill, i) => (
                <span key={i} className="dt-tag">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Color palette */}
          {cstyle !== "" && mcolor.length > 0 && (
            <div className="dt-meta-section">
              <p className="dt-meta-label">Color Palette</p>
              <div className="dt-swatches">
                {mcolor.map((color, i) => (
                  <div key={i} className="dt-swatch-item">
                    <div
                      className="dt-swatch"
                      style={{ backgroundColor: color }}
                    />
                    <span className="dt-swatch-hex">{color}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Content: overview + achievements ── */}
      <div className="dt-content-grid">
        <div className="dt-content-section">
          <p className="dt-section-label">Project Overview</p>
          <p className="dt-section-text">
            {pType === "ux" ? `UX Design: ${desc}` : desc}
          </p>
        </div>

        {achieve !== "" && (
          <div className="dt-content-section">
            <p className="dt-section-label">Key Achievements</p>
            <p className="dt-section-text">{achieve}</p>
          </div>
        )}
      </div>

      {/* ── Engineering Notes (replaces Challenges Faced) ── */}
      {engNotes && engNotes.length > 0 && (
        <div className="dt-eng-notes-wrap">
          <p className="dt-section-label">Engineering Notes</p>
          <div className="dt-eng-notes">
            {engNotes.map((note, i) => (
              <div key={i} className="dt-eng-note">
                <p className="dt-eng-note-title">{note.title}</p>
                <p className="dt-eng-note-body">{note.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Extra images ── */}
      {/* ── Extra images ── */}
      {extraimgs && extraimgs.length > 0 && (
        <div className="dt-extra-imgs-wrap">
          <p className="dt-section-label">Screenshots</p>
          <div className="dt-extra-imgs">
            {extraimgs.map((img, i) => (
              <img
                key={i}
                className="dt-extra-img"
                src={img}
                alt={`${thisProduct?.text} screenshot ${i + 1}`}
                onClick={() => setLightboxImg(img)}
              />
            ))}
          </div>

          {/* Lightbox */}
          {lightboxImg && (
            <div className="dt-lightbox" onClick={() => setLightboxImg(null)}>
              <button
                className="dt-lightbox-close"
                onClick={() => setLightboxImg(null)}
                aria-label="Close image"
              >
                ✕
              </button>
              <img
                className="dt-lightbox-img"
                src={lightboxImg}
                alt="Full size screenshot"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectDetail;
